/**
 * Application Constants
 * General constants used throughout the application
 */

/**
 * Local Storage Keys
 */
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'rib_auth_token',
  REFRESH_TOKEN: 'rib_refresh_token',
  USER_PREFERENCES: 'rib_user_preferences',
  THEME: 'rib_theme',
  LANGUAGE: 'rib_language',
  REMEMBER_ME: 'rib_remember_me',
  LAST_LOGIN: 'rib_last_login',
  FX_RATE_DATA: 'rib_fx_rate_data',
  QUOTE_DATA: 'rib_quote_data',
  PAYMENT_DETAILS: 'rib_payment_details',
} as const;

/**
 * Session Storage Keys
 */
export const SESSION_KEYS = {
  CURRENT_SESSION: 'rib_current_session',
  NAVIGATION_STATE: 'rib_navigation_state',
  FORM_DATA: 'rib_form_data',
} as const;

/**
 * HTTP Status Codes
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const;

/**
 * Application Messages
 */
export const MESSAGES = {
  SUCCESS: {
    LOGIN: 'Login successful',
    LOGOUT: 'Logout successful',
    SAVE: 'Data saved successfully',
    UPDATE: 'Data updated successfully',
    DELETE: 'Data deleted successfully',
    CREATE: 'Data created successfully',
  },
  ERROR: {
    GENERIC: 'An unexpected error occurred',
    NETWORK: 'Network error. Please check your connection',
    UNAUTHORIZED: 'You are not authorized to perform this action',
    NOT_FOUND: 'Requested resource not found',
    VALIDATION: 'Please check your input and try again',
    SESSION_EXPIRED: 'Your session has expired. Please login again',
  },
  CONFIRMATION: {
    DELETE: 'Are you sure you want to delete this item?',
    LOGOUT: 'Are you sure you want to logout?',
    UNSAVED_CHANGES: 'You have unsaved changes. Do you want to continue?',
  },
} as const;

/**
 * Date and Time Formats
 */
export const DATE_FORMATS = {
  SHORT: 'MM/dd/yyyy',
  LONG: 'MMMM dd, yyyy',
  WITH_TIME: 'MM/dd/yyyy HH:mm',
  TIME_ONLY: 'HH:mm',
  ISO: 'yyyy-MM-ddTHH:mm:ss',
} as const;

/**
 * Pagination Constants
 */
export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 10,
  PAGE_SIZE_OPTIONS: [5, 10, 25, 50, 100],
  MAX_PAGE_SIZE: 100,
} as const;

/**
 * File Upload Constants
 */
export const FILE_UPLOAD = {
  MAX_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.pdf'],
} as const;

/**
 * Validation Constants
 */
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 8,
  PASSWORD_MAX_LENGTH: 128,
  USERNAME_MIN_LENGTH: 3,
  USERNAME_MAX_LENGTH: 50,
  EMAIL_MAX_LENGTH: 254,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 15,
} as const;

/**
 * Theme Constants
 */
export const THEMES = {
  LIGHT: 'light',
  DARK: 'dark',
  AUTO: 'auto',
} as const;

/**
 * Language Constants
 */
export const LANGUAGES = {
  ENGLISH: 'en-US',
  SPANISH: 'es-ES',
  FRENCH: 'fr-FR',
} as const;

/**
 * Animation Durations (in milliseconds)
 */
export const ANIMATION_DURATION = {
  FAST: 150,
  NORMAL: 300,
  SLOW: 500,
} as const;

/**
 * Debounce Delays (in milliseconds)
 */
export const DEBOUNCE_DELAY = {
  SEARCH: 300,
  RESIZE: 100,
  SCROLL: 50,
} as const;

/**
 * Payment Constants
 */
export const PAYMENT_CONSTANTS = {
  // Hardcoded CIF ID as per requirement
  CIF_ID: '001Q22E3KWR8',

  // Beneficiary Types
  BENEFICIARY_TYPE: {
    DOMESTIC: 'DOMESTIC',
    INTERNATIONAL: 'INTERNATIONAL',
  },

  // Payment Types
  PAYMENT_TYPE: {
    DOMESTIC: 'DOMESTIC',
    INTERNATIONAL: 'INTERNATIONAL',
  },

  // Default Currency
  DEFAULT_CURRENCY: 'USD',

  // Supported Currencies
  CURRENCIES: [
    { code: 'USD', name: 'US Dollar', symbol: '$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'British Pound', symbol: '£' },
    { code: 'JPY', name: 'Japanese Yen', symbol: '¥' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'AED', name: 'UAE Dirham', symbol: 'AED' },
  ],

  // SWIFT Message Types
  SWIFT_MESSAGE_TYPES: [
    { code: 'MT103', name: 'Single Customer Credit Transfer' },
    { code: 'MT202', name: 'General Financial Institution Transfer' },
  ],

  // Default SWIFT Message Type
  DEFAULT_SWIFT_TYPE: 'MT103',

  // FX Rate Expiry (in minutes)
  FX_RATE_EXPIRY_MINUTES: 30,

  // Decimal precision for amounts
  AMOUNT_DECIMAL_PLACES: 2,

  // Decimal precision for FX rates
  FX_RATE_DECIMAL_PLACES: 6,
} as const;

/**
 * Payment Purpose Codes
 */
export const PAYMENT_PURPOSE_CODES = [
  { code: 'SALA', description: 'Salary Payment' },
  { code: 'PENS', description: 'Pension Payment' },
  { code: 'SUPP', description: 'Supplier Payment' },
  { code: 'TRAD', description: 'Trade Settlement' },
  { code: 'LOAN', description: 'Loan Payment' },
  { code: 'INVS', description: 'Investment' },
  { code: 'EDUC', description: 'Education' },
  { code: 'MEDI', description: 'Medical Treatment' },
  { code: 'TRVL', description: 'Travel' },
  { code: 'GIFT', description: 'Gift' },
  { code: 'DONA', description: 'Donation' },
  { code: 'OTHR', description: 'Other' },
] as const;

/**
 * Customer Information File (CIF) constants
 */
export const CIF_CONSTANTS = {
  DEFAULT_CIF_ID: '001Q22E3KWR8',
} as const;

/**
 * API Response constants
 */
export const API_RESPONSE = {
  SUCCESS_STATUS: 'success',
  ERROR_STATUS: 'error',
} as const;

/**
 * OTP Configuration for International Payments
 */
export const OTP_CONFIG = {
  /** Valid OTP for mock verification */
  VALID_OTP: '123456',
  /** OTP validity period in seconds (5 minutes) */
  VALIDITY_SECONDS: 300,
  /** Maximum number of resend attempts */
  MAX_RESEND_ATTEMPTS: 3,
  /** OTP length */
  OTP_LENGTH: 6,
  /** Countdown timer for resend (in seconds) */
  RESEND_COUNTDOWN_SECONDS: 59,
} as const;

/**
 * International Payment Flow Steps
 */
export const PAYMENT_FLOW_STEPS = {
  SELECT_BENEFICIARY: 1,
  PAYMENT_DETAILS: 2,
  REVIEW: 3,
  OTP_VERIFICATION: 4,
  SUCCESS: 5,
} as const;
